/**
 * Offline Service - خدمة دعم العمل دون اتصال
 * يحفظ الفواتير محلياً عند عدم وجود اتصال ويقوم بمزامنتها عند عودة الاتصال
 */

import { apiClient } from '../api/client'
import { API_ENDPOINTS } from '../api/endpoints'
import { secureStorage } from '../utils/secureStorage'

export interface OfflineInvoice {
  id: string // Local temporary ID (UUID)
  data: any // Invoice data
  status: 'pending' | 'syncing' | 'synced' | 'error'
  action: 'create' | 'update' | 'delete'
  timestamp: number
  retryCount: number
  error?: string
}

export interface OfflineQueue {
  invoices: OfflineInvoice[]
  lastSync: number | null
}

const STORAGE_KEY = 'offline_invoices_queue'
const MAX_RETRY_COUNT = 3
const SYNC_INTERVAL = 30000 // 30 seconds

class OfflineService {
  private queue: OfflineQueue = { invoices: [], lastSync: null }
  private isOnline: boolean = navigator.onLine
  private syncInterval: NodeJS.Timeout | null = null

  constructor() {
    this.loadQueue()
    // Offline mode disabled - don't setup listeners or auto sync
    // this.setupEventListeners()
    // this.startAutoSync()
  }

  /**
   * Load queue from localStorage
   */
  private loadQueue(): void {
    try {
      const stored = secureStorage.getItem(STORAGE_KEY)
      if (stored) {
        this.queue = JSON.parse(stored)
      }
    } catch (error) {
      console.error('Error loading offline queue:', error)
      this.queue = { invoices: [], lastSync: null }
    }
  }

  /**
   * Save queue to localStorage
   */
  private saveQueue(): void {
    try {
      secureStorage.setItem(STORAGE_KEY, JSON.stringify(this.queue))
    } catch (error) {
      console.error('Error saving offline queue:', error)
    }
  }

  /**
   * Setup online/offline event listeners
   * @deprecated Currently disabled - offline mode is disabled
   */
  private _setupEventListeners(): void {
    window.addEventListener('online', () => {
      this.isOnline = true
      console.log('Connection restored, starting sync...')
      
      // Show notification
      if (typeof window !== 'undefined' && (window as any).toast) {
        const toast = (window as any).toast
        toast.success('تم استعادة الاتصال. سيتم مزامنة الفواتير تلقائياً.', {
          duration: 4000,
          icon: '✅',
        })
      }
      
      this.syncAll()
    })

    window.addEventListener('offline', () => {
      this.isOnline = false
      console.log('Connection lost, working offline...')
      
      // Show notification
      if (typeof window !== 'undefined' && (window as any).toast) {
        const toast = (window as any).toast
        toast.error('انقطع الاتصال بالإنترنت. سيتم حفظ الفواتير محلياً.', {
          duration: 5000,
          icon: '⚠️',
        })
      }
    })
  }

  /**
   * Start automatic sync interval
   * @deprecated Currently disabled - offline mode is disabled
   */
  private _startAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval)
    }

    this.syncInterval = setInterval(() => {
      if (this.isOnline && this.queue.invoices.length > 0) {
        this.syncAll()
      }
    }, SYNC_INTERVAL)
  }

  /**
   * Check if online
   */
  isConnected(): boolean {
    return this.isOnline
  }

  /**
   * Add invoice to offline queue
   * DISABLED: Offline mode is disabled
   */
  addInvoice(_invoice: any, _action: 'create' | 'update' | 'delete' = 'create'): string {
    // Offline mode disabled - return empty string and don't queue
    console.warn('Offline mode is disabled. Invoice will not be queued.')
    return ''
    
    // const offlineInvoice: OfflineInvoice = {
    //   id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    //   data: invoice,
    //   status: 'pending',
    //   action,
    //   timestamp: Date.now(),
    //   retryCount: 0,
    // }

    // this.queue.invoices.push(offlineInvoice)
    // this.saveQueue()

    // // Try to sync immediately if online
    // if (this.isOnline) {
    //   this.syncInvoice(offlineInvoice.id)
    // }

    // return offlineInvoice.id
  }

  /**
   * Get all pending invoices
   */
  getPendingInvoices(): OfflineInvoice[] {
    return this.queue.invoices.filter(
      inv => inv.status === 'pending' || inv.status === 'error'
    )
  }

  /**
   * Get all offline invoices
   */
  getAllOfflineInvoices(): OfflineInvoice[] {
    return this.queue.invoices
  }

  /**
   * Get invoice by local ID
   */
  getInvoice(localId: string): OfflineInvoice | undefined {
    return this.queue.invoices.find(inv => inv.id === localId)
  }

  /**
   * Remove invoice from queue
   */
  removeInvoice(localId: string): void {
    this.queue.invoices = this.queue.invoices.filter(inv => inv.id !== localId)
    this.saveQueue()
  }

  /**
   * Update invoice in queue
   */
  updateInvoice(localId: string, updates: Partial<OfflineInvoice>): void {
    const index = this.queue.invoices.findIndex(inv => inv.id === localId)
    if (index !== -1) {
      this.queue.invoices[index] = { ...this.queue.invoices[index], ...updates }
      this.saveQueue()
    }
  }

  /**
   * Clean invoice data before sending to API
   * Removes computed fields and ensures proper format
   */
  private cleanInvoiceData(data: any): any {
    // Create a clean copy of the data
    const cleaned: any = { ...data }
    
    // Remove computed fields that shouldn't be sent to API
    delete cleaned.totals
    delete cleaned.customerName
    delete cleaned.customerAddress
    delete cleaned.customerPhone
    delete cleaned.customerTaxNumber
    
    // Try to extract storeId from invoiceDetails if not present at top level
    if (!cleaned.storeId && cleaned.invoiceDetails && Array.isArray(cleaned.invoiceDetails)) {
      const firstDetailWithStore = cleaned.invoiceDetails.find((detail: any) => detail.storeId)
      if (firstDetailWithStore?.storeId) {
        cleaned.storeId = firstDetailWithStore.storeId
        console.log('Extracted storeId from invoiceDetails:', cleaned.storeId)
      }
    }
    
    // Ensure invoiceDetails is properly formatted
    if (cleaned.invoiceDetails && Array.isArray(cleaned.invoiceDetails)) {
      cleaned.invoiceDetails = cleaned.invoiceDetails.map((detail: any) => {
        const cleanedDetail: any = { ...detail }
        
        // Ensure storeId is set in each detail (use from top level if not in detail)
        if (!cleanedDetail.storeId && cleaned.storeId) {
          cleanedDetail.storeId = cleaned.storeId
        }
        
        // Remove computed fields from details
        delete cleanedDetail.netBeforeTax
        delete cleanedDetail.net
        delete cleanedDetail.lastCustomerPrice
        delete cleanedDetail.lastCustomerPriceDate
        delete cleanedDetail.lastCustomerInvoiceSerial
        delete cleanedDetail.availableQuantity
        delete cleanedDetail.itemName
        delete cleanedDetail.itemCode
        delete cleanedDetail.barcode
        delete cleanedDetail.unitName
        delete cleanedDetail.isWeightBased
        
        // Ensure required fields are present
        if (!cleanedDetail.itemId) {
          console.warn('Invoice detail missing itemId:', cleanedDetail)
        }
        if (!cleanedDetail.quantity || cleanedDetail.quantity <= 0) {
          console.warn('Invoice detail has invalid quantity:', cleanedDetail)
        }
        
        return cleanedDetail
      }).filter((detail: any) => detail.itemId && detail.quantity > 0)
    }
    
    // Validate and ensure required fields are present
    if (!cleaned.branchId) {
      // Try to get from localStorage as fallback
      const defaultBranchId = localStorage.getItem('branchId')
      if (defaultBranchId) {
        cleaned.branchId = defaultBranchId
        console.log('Using default branchId from localStorage:', cleaned.branchId)
      } else {
        throw new Error('Branch ID is required and not found in invoice data or localStorage')
      }
    }
    
    if (!cleaned.storeId) {
      // Try to get from localStorage as fallback
      const defaultStoreId = localStorage.getItem('storeId')
      if (defaultStoreId) {
        cleaned.storeId = defaultStoreId
        console.log('Using default storeId from localStorage:', cleaned.storeId)
      } else {
        throw new Error('Store ID is required and not found in invoice data, invoiceDetails, or localStorage')
      }
    }
    
    // Ensure storeId is a string (not empty)
    if (typeof cleaned.storeId === 'string' && cleaned.storeId.trim() === '') {
      throw new Error('Store ID cannot be empty')
    }
    
    // Ensure branchId is a string (not empty)
    if (typeof cleaned.branchId === 'string' && cleaned.branchId.trim() === '') {
      throw new Error('Branch ID cannot be empty')
    }
    
    return cleaned
  }

  /**
   * Sync single invoice
   */
  async syncInvoice(localId: string): Promise<boolean> {
    const invoice = this.getInvoice(localId)
    if (!invoice) {
      console.warn(`Invoice ${localId} not found in queue`)
      return false
    }

    if (invoice.status === 'syncing') {
      console.log(`Invoice ${localId} is already syncing`)
      return false // Already syncing
    }

    if (!this.isOnline) {
      console.log(`Cannot sync invoice ${localId}: offline`)
      return false
    }

    // Update status to syncing
    this.updateInvoice(localId, { status: 'syncing' })

    try {
      console.log(`Syncing invoice ${localId} (action: ${invoice.action})`)
      console.log('Original invoice data:', invoice.data)
      
      // Clean the invoice data before sending
      const cleanedData = this.cleanInvoiceData(invoice.data)
      console.log('Cleaned invoice data:', cleanedData)
      
      let result: any

      switch (invoice.action) {
        case 'create':
          result = await apiClient.post(API_ENDPOINTS.invoices.create, cleanedData)
          console.log('Invoice created successfully:', result.data)
          break
        case 'update':
          if (invoice.data.id) {
            result = await apiClient.put(
              API_ENDPOINTS.invoices.update(invoice.data.id),
              cleanedData
            )
            console.log('Invoice updated successfully:', result.data)
          } else {
            throw new Error('Invoice ID is required for update')
          }
          break
        case 'delete':
          if (invoice.data.id) {
            await apiClient.delete(API_ENDPOINTS.invoices.delete(invoice.data.id))
            console.log('Invoice deleted successfully')
          } else {
            throw new Error('Invoice ID is required for delete')
          }
          break
      }

      // Mark as synced
      this.updateInvoice(localId, {
        status: 'synced',
        error: undefined,
      })

      console.log(`Invoice ${localId} synced successfully`)

      // Remove from queue after a delay (to allow UI to show success)
      setTimeout(() => {
        this.removeInvoice(localId)
      }, 5000)

      return true
    } catch (error: any) {
      const retryCount = invoice.retryCount + 1
      const shouldRetry = retryCount < MAX_RETRY_COUNT
      
      // Extract error message
      let errorMessage = 'Sync failed'
      if (error?.response?.data) {
        const errorData = error.response.data
        errorMessage = errorData.message || errorData.error || JSON.stringify(errorData)
      } else if (error?.message) {
        errorMessage = error.message
      }
      
      // Check if it's a validation error (missing required fields)
      const isValidationError = errorMessage.includes('required') || 
                                errorMessage.includes('Store ID') || 
                                errorMessage.includes('Branch ID')
      
      console.error(`Failed to sync invoice ${localId}:`, errorMessage)
      console.error('Error details:', error)
      console.error('Invoice data that failed:', invoice.data)
      
      // If it's a validation error and we haven't tried to fix it, mark as error immediately
      // Don't retry validation errors as they won't fix themselves
      if (isValidationError) {
        console.error(`Validation error detected for invoice ${localId}. Marking as error (won't retry).`)
        this.updateInvoice(localId, {
          status: 'error',
          retryCount: MAX_RETRY_COUNT, // Mark as max retries to prevent further attempts
          error: `Validation Error: ${errorMessage}. Please check the invoice data.`,
        })
        return false
      }

      this.updateInvoice(localId, {
        status: shouldRetry ? 'pending' : 'error',
        retryCount,
        error: errorMessage,
      })

      if (!shouldRetry) {
        console.error(`Failed to sync invoice ${localId} after ${MAX_RETRY_COUNT} attempts. Final error: ${errorMessage}`)
      } else {
        console.log(`Will retry invoice ${localId} (attempt ${retryCount}/${MAX_RETRY_COUNT})`)
      }

      return false
    }
  }

  /**
   * Sync all pending invoices
   */
  async syncAll(): Promise<{ success: number; failed: number }> {
    if (!this.isOnline) {
      return { success: 0, failed: 0 }
    }

    const pending = this.getPendingInvoices()
    let success = 0
    let failed = 0

    // Sync invoices sequentially to avoid overwhelming the server
    for (const invoice of pending) {
      const result = await this.syncInvoice(invoice.id)
      if (result) {
        success++
      } else {
        failed++
      }
      
      // Small delay between syncs
      await new Promise(resolve => setTimeout(resolve, 500))
    }

    this.queue.lastSync = Date.now()
    this.saveQueue()

    return { success, failed }
  }

  /**
   * Clear all synced invoices
   */
  clearSynced(): void {
    this.queue.invoices = this.queue.invoices.filter(inv => inv.status !== 'synced')
    this.saveQueue()
  }

  /**
   * Clear all invoices (use with caution)
   */
  clearAll(): void {
    this.queue = { invoices: [], lastSync: null }
    this.saveQueue()
  }

  /**
   * Get queue statistics
   */
  getStats(): {
    total: number
    pending: number
    syncing: number
    synced: number
    error: number
    lastSync: number | null
  } {
    return {
      total: this.queue.invoices.length,
      pending: this.queue.invoices.filter(inv => inv.status === 'pending').length,
      syncing: this.queue.invoices.filter(inv => inv.status === 'syncing').length,
      synced: this.queue.invoices.filter(inv => inv.status === 'synced').length,
      error: this.queue.invoices.filter(inv => inv.status === 'error').length,
      lastSync: this.queue.lastSync,
    }
  }
}

// Singleton instance
export const offlineService = new OfflineService()

export default offlineService

